import * as React from "react";
import { IconButton, Tooltip, AlertColor } from "@mui/material";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import { useSelector } from "react-redux";
import { RootState } from "../../../app/store";
import { AREA_PREFERENCE_TYPE_IDS } from "../../../app/slices/areaPreferencesSlice";
import { useSetUserPreferenceMutation } from "../../../services/cclTokenedUserPreferenceApi";
import { UserClaimsService } from "../../../services/currentUserService/currentUserService";
import CclAlertSnackbar from "../cclAlertSnackbar";

export interface CclSavePreferencesButtonProps {
    area: string;
}

const CclSavePreferencesButton: React.FC<CclSavePreferencesButtonProps> = ({ area }) => {
    const [saveUserPref] = useSetUserPreferenceMutation();
    const [showSnackbar, setShowSnackbar] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor>("info");
    const [saving, setSaving] = React.useState(false);

    const areaData = useSelector((state: RootState) => state.areaPreferences?.areas?.[area]);

    const handleSave = () => {
        const claimsService = new UserClaimsService();
        const preferenceTypeId = AREA_PREFERENCE_TYPE_IDS[area];
        if (preferenceTypeId == null) return;

        setSaving(true);
        saveUserPref({
            email: claimsService.GetCurrentUserEmail(),
            userId: claimsService.GetCurrentUserId(),
            data: JSON.stringify(areaData ?? {}),
            preferenceId: preferenceTypeId,
        })
            .unwrap()
            .then(() => {
                setSnackbarMessage("Preferences Saved");
                setSnackbarSeverity("success");
                setShowSnackbar(true);
            })
            .catch(() => {
                setSnackbarMessage("Save Preferences Failed");
                setSnackbarSeverity("error");
                setShowSnackbar(true);
            })
            .finally(() => setSaving(false));
    };

    return (
        <>
            {showSnackbar ? (
                <CclAlertSnackbar
                    open={true}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                    onClose={() => setShowSnackbar(false)}
                />
            ) : null}
            <Tooltip title="Save as default view">
                <IconButton
                    size="small"
                    onClick={handleSave}
                    disabled={saving}
                    color="secondary"
                    sx={{ alignSelf: "flex-end" }}
                >
                    {areaData && Object.keys(areaData).length > 0 ? (
                        <BookmarkIcon fontSize="small" />
                    ) : (
                        <BookmarkBorderIcon fontSize="small" />
                    )}
                </IconButton>
            </Tooltip>
        </>
    );
};

export default CclSavePreferencesButton;
